import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { CollectionService } from '../../core/services/collection.service';
import { NavigationService } from '../../core/services/navigation.service';
import { ScrollService } from '../../core/services/scroll.service';
import { SectorIndustryFilterService, MarketCapFilter, DiscountFilter } from '../../core/services/sector-industry-filter.service';
import { LoggingService } from '../../core/services/logging.service';
import { Candidate } from '../../shared/models/candidate-model';
import { StockTableComponent, TableSortEvent } from '../../shared/ui/tables/stock-table/stock-table.component';
import { GoBackButtonComponent } from '../../shared/ui/go-back-button/go-back-button.component';
import { IndustryListSkeletonComponent } from './skeleton/industry-list-skeleton.component';

@Component({
  selector: 'app-industry-list',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, StockTableComponent, GoBackButtonComponent, IndustryListSkeletonComponent],
  templateUrl: './industry-list.component.html',
  styleUrls: ['./industry-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IndustryListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private collectionSvc = inject(CollectionService);
  private navigationService = inject(NavigationService);
  private scrollService = inject(ScrollService);
  private filterService = inject(SectorIndustryFilterService);
  private logger = inject(LoggingService);
  private pendingScrollRestoreTimeoutId: ReturnType<typeof setTimeout> | null = null;

  allCandidates = signal<Candidate[]>([]);
  industryName = signal<string>('');
  loading = signal(true);
  error = signal<string | null>(null);

  // Sort state
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number | undefined>(undefined);

  // Filter state from service
  selectedMarketCap = this.filterService.marketCapFilter;
  selectedDiscount = this.filterService.discountFilter;

  // Market cap filter options
  marketCapOptions = [
    { label: 'Any', value: 'any' as MarketCapFilter },
    { label: 'Mega Cap ($200B+)', value: 'mega' as MarketCapFilter },
    { label: 'Large Cap ($10B-$200B)', value: 'large' as MarketCapFilter },
    { label: 'Mid Cap ($2B-$10B)', value: 'mid' as MarketCapFilter },
    { label: 'Small Cap ($300M-$2B)', value: 'small' as MarketCapFilter },
    { label: 'Micro Cap ($50M-$300M)', value: 'micro' as MarketCapFilter }
  ];

  // Discount filter options
  discountOptions = [
    { label: 'Any', value: 'any' as DiscountFilter },
    { label: '> 20%', value: '20' as DiscountFilter },
    { label: '> 50%', value: '50' as DiscountFilter },
    { label: '> 80%', value: '80' as DiscountFilter }
  ];

  // Filtered candidates based on filter selections
  candidates = computed(() => {
    let filtered = this.allCandidates();

    // Apply market cap filter
    const marketCapRange = this.filterService.getMarketCapRange(this.selectedMarketCap());
    if (marketCapRange) {
      filtered = filtered.filter(c =>
        c.marketCap >= marketCapRange.min && c.marketCap < marketCapRange.max
      );
    }

    // Apply discount filter
    const minDiscount = this.filterService.getMinimumDiscount(this.selectedDiscount());
    if (minDiscount !== null) {
      filtered = filtered.filter(c =>
        c.discount !== undefined && c.discount >= minDiscount
      );
    }

    return filtered;
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.clearPendingScrollRestore());
  }

  ngOnInit() {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const industryParam = params['industryName'];
      if (industryParam) {
        // Clear filters when navigating to a new industry
        this.filterService.resetToDefaults();

        // Convert URL-safe name back to readable format
        const readableIndustryName = this.decodeIndustryName(industryParam);
        this.industryName.set(readableIndustryName);

        // Restore sort state for this industry
        const routeKey = `/industries/${industryParam}`;
        const savedState = this.navigationService.getListState(routeKey);
        if (savedState) {
          this.sortField.set(savedState.sortField);
          this.sortOrder.set(savedState.sortOrder);
        }

        this.loadCandidates(readableIndustryName);
      }
    });
  }

  private decodeIndustryName(urlSafeName: string): string {
    return urlSafeName.replace(/~/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).trim();
  }

  private get routeKey(): string {
    return `/industries/${this.route.snapshot.params['industryName']}`;
  }

  private loadCandidates(industryName: string) {
    this.loading.set(true);
    this.error.set(null);

    // Capture at call time to avoid stale snapshot reads in async callbacks
    const currentRouteKey = this.routeKey;
    this.collectionSvc.getCandidatesByIndustry(industryName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.allCandidates.set(data);
        },
        error: (err) => {
          this.logger.error('Error loading candidates for industry:', industryName, err);
          this.error.set('Failed to load candidates for this industry');
          this.loading.set(false);
        },
        complete: () => {
          this.loading.set(false);
          // Restore scroll position when returning from a stock detail page
          this.clearPendingScrollRestore();
          this.pendingScrollRestoreTimeoutId = this.scrollService.consumeAndScheduleRestore(currentRouteKey);
        }
      });
  }

  private clearPendingScrollRestore(): void {
    if (this.pendingScrollRestoreTimeoutId !== null) {
      clearTimeout(this.pendingScrollRestoreTimeoutId);
      this.pendingScrollRestoreTimeoutId = null;
    }
  }

  onStockClick(stock: Candidate) {
    // Save scroll position so it can be restored when the user navigates back
    this.scrollService.savePendingRestore(this.routeKey, this.scrollService.getScrollTop());
    this.navigationService.saveStockDetailListContext({
      sourceRoute: this.routeKey,
      sourceType: 'industry',
      listTitle: this.industryName(),
      selectedTicker: stock.ticker,
      items: this.candidates().map(candidate => ({
        ticker: candidate.ticker,
        name: candidate.name,
        discount: candidate.discount,
        currentPrice: candidate.currentPrice,
        stickerPrice: candidate.stickerPrice
      }))
    });
    this.router.navigate(['/stocks', stock.ticker]);
  }

  // Navigate back to previous page
  goBack(): void {
    this.location.back();
  }

  // Handle sort changes from table
  onSortChanged(event: TableSortEvent): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order);

    // Save sort state using the industry parameter from route snapshot
    const industryParam = this.route.snapshot.params['industryName'];
    if (industryParam) {
      this.navigationService.saveListState(this.routeKey, {
        sortField: event.field,
        sortOrder: event.order
      });
    }
  }

  // Handle market cap filter change
  onMarketCapChange(): void {
    this.filterService.updateMarketCap(this.selectedMarketCap());
  }

  // Handle discount filter change
  onDiscountChange(): void {
    this.filterService.updateDiscount(this.selectedDiscount());
  }

  // Check if any filters are active
  hasActiveFilters(): boolean {
    return this.selectedMarketCap() !== 'any' || this.selectedDiscount() !== 'any';
  }

  // Clear all filters
  clearFilters(): void {
    this.filterService.resetToDefaults();
  }
}
